import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { resourcesCopy } from '@/copy/resources'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { SelectField, TextField } from '@/ui/Field/Field'
import { Skeleton } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { ConfirmDialog } from '@/ui/Modal/Modal'
import { type ResourceScope, resourcesFor } from '../api/resources'
import { useResourceKinds } from '../hooks/useResourceKinds'
import { useToastStore } from '../store/toast'
import styles from './ResourceFormContent.module.css'
import { getResourceForm } from './resource-forms'

interface Props {
  scope: ResourceScope
  /** Omit for create mode. */
  resourceId?: string
  /** Called after successful create/update/delete or when user cancels. */
  onDone: () => void
}

/**
 * The resource form body — kind select, fields, submit/cancel/delete. Split out
 * from `ResourceFormPage` so the page owns the shell, crumbs, and back
 * navigation while this stays a plain form. `onDone` covers create, update,
 * delete, and cancel alike.
 */
export function ResourceFormContent({ scope, resourceId, onDone }: Props) {
  const isEdit = !!resourceId
  const rid = resourceId ?? ''
  const queryClient = useQueryClient()
  const addToast = useToastStore((s) => s.addToast)
  const queryKey = ['resources', scope.kind, scope.id]
  const api = resourcesFor(scope)

  const listQuery = useQuery({
    queryKey,
    queryFn: () => api.list(),
    staleTime: 10_000,
  })
  const listData = listQuery.data

  const kindsQuery = useResourceKinds()
  const registered = kindsQuery.data

  const loading = isEdit ? listQuery.isLoading : kindsQuery.isLoading
  const failed = isEdit ? listQuery.isError : kindsQuery.isError
  const showSkeleton = useSkeletonDelay(loading)

  const existing = isEdit
    ? listData?.resources.find((r) => r.id === resourceId)
    : undefined
  // Offered kinds are the backend-registered kinds filtered to this scope;
  // display order is the registry's registration order.
  const availableKinds = (registered ?? [])
    .filter((k) => k.scopes.includes(scope.kind))
    .map((k) => k.kind)

  const [kind, setKind] = useState<string>('')
  const [name, setName] = useState('')
  const [fields, setFields] = useState<Record<string, string>>({})
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const effectiveKind = isEdit ? kind : kind || availableKinds[0] || ''
  const kindHelp = registered?.find((k) => k.kind === effectiveKind)?.display
    .description
  const desc = getResourceForm(effectiveKind)

  useEffect(() => {
    if (existing && desc) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- derived state from existing resource
      setKind(existing.kind)
      setName(existing.name)
      setFields(desc.fromResource(existing.data))
    }
  }, [existing, desc])

  function set(key: string, value: string) {
    setFields((f) => ({ ...f, [key]: value }))
  }

  const invalidate = () => queryClient.invalidateQueries({ queryKey })

  const createMutation = useMutation({
    mutationFn: async () => {
      // Kinds with an out-of-band create (github's one-click App setup)
      // own their submission; `false` falls back to the standard create.
      // Kinds without one always take the standard create.
      if (desc?.createVia) {
        const via = await desc.createVia(fields, scope)
        if (via !== false) return
      }
      return api.create(effectiveKind, name.trim(), desc?.toBody(fields) ?? {})
    },
    onSuccess: () => {
      void invalidate()
      onDone()
    },
  })

  const updateMutation = useMutation({
    mutationFn: () => {
      const newName = name.trim()
      const changedName = newName !== existing?.name ? newName : undefined
      const data = desc?.skipDataOnUpdate ? undefined : desc?.toBody(fields)
      return api.update(rid, data, changedName)
    },
    onSuccess: () => {
      void invalidate()
      onDone()
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(rid),
    onSuccess: () => {
      void invalidate()
      onDone()
    },
    onError: (e: Error) =>
      addToast(resourcesCopy.form.deleteFailed(e.message), 'error'),
  })

  const isPending = createMutation.isPending || updateMutation.isPending
  const error =
    createMutation.error ?? updateMutation.error ?? deleteMutation.error

  const nameByFlow = !isEdit && (desc?.namedByFlow?.(fields) ?? false)
  const nameMissing = !nameByFlow && !name.trim()

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (nameMissing) return
    if (isEdit) updateMutation.mutate()
    else createMutation.mutate()
  }

  if (loading) {
    return <div aria-busy="true">{showSkeleton && <Skeleton lines={4} />}</div>
  }

  if (failed) {
    return (
      <Alert tone="danger">
        <Inline gap="space-8" align="baseline">
          {resourcesCopy.form.loadFailed}
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              void (isEdit ? listQuery.refetch() : kindsQuery.refetch())
            }
          >
            {resourcesCopy.retry}
          </Button>
        </Inline>
      </Alert>
    )
  }

  return (
    <Stack gap="space-32">
      <Stack
        as="form"
        gap="space-16"
        className={styles.form}
        onSubmit={handleSubmit}
      >
        {!isEdit && (
          <SelectField
            id="res-kind"
            label={resourcesCopy.form.kind}
            value={effectiveKind}
            onValue={(k) => {
              setKind(k)
              setFields({})
            }}
            help={kindHelp}
          >
            {availableKinds.map((k) => (
              <option key={k} value={k}>
                {registered?.find((r) => r.kind === k)?.display.label ?? k}
              </option>
            ))}
          </SelectField>
        )}

        {!nameByFlow && (
          <TextField
            id="res-name"
            label={resourcesCopy.form.name}
            value={name}
            onValue={setName}
            inputProps={{
              autoFocus: true,
            }}
          />
        )}

        {/* Kind-specific fields come from the per-kind descriptor. */}
        {desc?.Fields && (
          <desc.Fields
            fields={fields}
            set={set}
            isEdit={isEdit}
            scope={scope}
          />
        )}

        {/* Out-of-band edit UI (e.g. todo items), owned by the kind's descriptor. */}
        {isEdit && existing && desc?.EditExtras && (
          <desc.EditExtras
            scope={scope}
            resourceId={rid}
            data={existing.data}
          />
        )}

        {error && (
          <Alert tone="danger">
            {error instanceof Error
              ? error.message
              : resourcesCopy.form.requestFailed}
          </Alert>
        )}

        <Inline gap="space-8">
          <Button
            type="submit"
            variant="primary"
            disabled={nameMissing}
            loading={isPending}
          >
            {isEdit ? resourcesCopy.form.save : resourcesCopy.add}
          </Button>
          <Button variant="outline" onClick={onDone}>
            {resourcesCopy.form.cancel}
          </Button>
        </Inline>
      </Stack>

      {isEdit && (
        <div>
          <Button variant="outline" onClick={() => setShowDeleteConfirm(true)}>
            {resourcesCopy.form.deleteResource}
          </Button>
        </div>
      )}

      <ConfirmDialog
        open={showDeleteConfirm}
        title={resourcesCopy.form.deleteTitle}
        tone="danger"
        description={resourcesCopy.form.deleteDescription(existing?.name)}
        confirmLabel={resourcesCopy.form.deleteConfirm}
        loading={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate()}
        onClose={() => setShowDeleteConfirm(false)}
      />
    </Stack>
  )
}
